import React, { Fragment } from "react";
import { observer, inject } from "mobx-react";
import DataGrid from '@dataGrid';
import { autorun } from "mobx";
import { isEqual } from "lodash/lang";
import GridConfig from "./gridConfig";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { getDataWithQueryString, postData } from "../../../../api/componentAPIs";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import StatusBar from '@dataGrid/components/StatusBar';
import Events from "../../../../utilities/EventEmitter";
import { getFilteredData } from "../../../common/filterUtilities";
import appConstants from "../../../../appConstants";
import { getCustomerManagementGeocodingData } from "../../Common/Geocoding/utils";
import GeoCodingModal from "../../Common/Geocoding/GeoCodingModal";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../utils";
import { withAppConfig } from "../../../../appConfigContext";
import { getUniqueShipperFilterData, getShipperGridFeatureFlags } from "../utils";

@inject("IBPlanningStore")
@observer
class Shippers extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig({
      userConfig: this.props.userData.userProfile?.columnPref,
      featureFlags: getShipperGridFeatureFlags({ configJSON: this.props.configJSON }),
    });
    this.state = {
      quickFilterText: '',
      shipperData: null,
      terminalId: this.props.userData.userProfile.terminal.id,
      quickFilter: null,
      geoCodeCustomer: null
    };
    this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
    this.gridContext = {
      specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
    };
    this.gridHeaderId = `gridHeader_${this.props.glContainer._config.id}`;
    this.selectedShippers = [];
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.glEventHub.on(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.on(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.on("updateShipper", this.updateShipper);
    this.props.glEventHub.on(
      "updateQuickFilter",
      this.updateShipperQuickFilter
    );
    this.getShipperData();
    this.props.glEventHub.on(
      `activeTabSwitched_${appConstants.gridNames.ShipperGrid}`,
      this.onSelectionChanged
    );
    this.props.glEventHub.on(
      `${this.gridHeaderId}_refresh`,
      this.syncShipperOnDemand
    );
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.serviceLevelList,
          this.props.IBPlanningStore.serviceLevelList
        )
      ) {
        this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
        this.gridContext = {
          specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
        };
      }
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.glEventHub.off(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.off(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.off("updateShipper", this.updateShipper);
    this.props.glEventHub.off(
      "updateQuickFilter",
      this.updateShipperQuickFilter
    );
    this.props.glEventHub.off(
      `activeTabSwitched_${appConstants.gridNames.ShipperGrid}`,
      this.onSelectionChanged
    );
    this.props.glEventHub.off(
      `${this.gridHeaderId}_refresh`,
      this.syncShipperOnDemand
    );
    this.autorunPriorityShipmentList?.();
  }

  updateShipperQuickFilter = (quickFilterData) => {
    if (
      quickFilterData.filterName ===
      appConstants.IBPLANNING_QUICKFILTER_NAMES.SHIPPER_GRID
    ) {
      this.setState({
        quickFilter: quickFilterData.eventData,
      });
    }
  };

  syncShipperOnDemand = () => {
    const payload = {
      terminalCode: this.props.userData.userProfile.terminal.code,
      terminalId: this.props.userData.userProfile.terminal.id
    };
    this.props.glEventHub.emit(`${this.gridHeaderId}_refreshInProgress`, true);
    postData('SyncShipperOnDemand', payload)
      .then((response) => {
        const status = response?.status;
        if (status >= 200 && status < 400) {
          this.getShipperData(false);  
          this.props.enqueueSnackbar('Shippers synced successfully', {
            variant: 'success'
          });
          this.props.glEventHub.emit(`${this.gridHeaderId}_refreshInProgress`, false);
        } else {
          this.handleShipperSyncError(response);
        }
      })
      .catch((error) => {
        this.handleShipperSyncError();
      });
  };

  handleShipperSyncError = (error) => {
    if (error.statusCode === 403) {
      return;
    }
    const errorMessage =
      error?.response?.data?.error?.message ||
      error?.response?.data?.error?.details ||
      error?.message;
    const defaultErrorMessage = 'Failed to sync shippers. Please try again later';
    this.props.enqueueSnackbar(errorMessage || defaultErrorMessage, {
      variant: 'error'
    });
    this.props.glEventHub.emit(`${this.gridHeaderId}_refreshInProgress`, false);
  };

  onGeocodeCustomer = () => {
    const selectedShippers = this.gridApi.getSelectedRows();
    if (!selectedShippers.length) {
      return;
    }

    this.setState({
      geoCodeCustomer: {
        ...getCustomerManagementGeocodingData(
          selectedShippers[0],
          appConstants.CUSTOMER_TYPE.PICKUP
        ),
        proId: "",
      },
    });
  };

  updateShipper = (updatedCustomers) => {
    const { shipperData } = this.state;
    updatedCustomers.map((customer) => {
      const index = shipperData?.findIndex(
        (c) => c?.pickupDetails?.rmaxShipperCode === customer?.pickupDetails?.rmaxShipperCode
      );
      if (shipperData) {
        shipperData[index] = customer;
      }
    });
    this.setState({
      shipperData,
    }, this.updateQuickFilterData);
  };
  
  updateQuickFilterData = () => {
    this.props.glEventHub.emit("updatedShipperData", getUniqueShipperFilterData(this.state.shipperData));
  };

  static getDerivedStateFromProps(nextProps, state) {
    if (nextProps.userData.userProfile.terminal.id !== state.terminalId) {
      return {
        terminalId: nextProps.userData.userProfile.terminal.id,
        shipperData: null,
      };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.userData.userProfile.terminal.id !==
      this.props.userData.userProfile.terminal.id
    ) {
      this.getShipperData();
    }
  }

  getShipperData = ( resetDataOnGrid = true ) => {
    resetDataOnGrid &&
      this.setState({
        shipperData: null
      });

    getDataWithQueryString(
      "GetCustomerDetail",
      `terminalId=${this.props.userData.userProfile.terminal.id}&shouldOnlyReturnShippers=true`
    ).then((response) => {
      if (!this._isMounted) {
        return;
      }
      if (response.status === 200) {
        this.setState({
          shipperData: response?.data ?? [],
        }, this.updateQuickFilterData);
      } else {
        this.setState({
          shipperData: [],
        }, this.updateQuickFilterData);
      }
    });
  };

  onSearchTextChanged = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.gridApi.runCommand = this.runCommand;
  };

  onSelectionChanged = () => {
    if(this.props.glContainer.tab.isActive){
      this.selectedShippers = this.gridApi.getSelectedRows();
      this.props.glEventHub.emit(`customerSelectedonGrid`, this.selectedShippers);
    }
  };

  onCellClicked = (params) => {
    if(params.column.colId === "name"){
      Events.emit(
        'OpenEditCustomerDialog',
        params?.node?.data?.pickupDetails?.rmaxShipperCode,
        'pickup'
      );
    }
  };

  constructStatusBar = (shipperList) => {
    return [
      {
        label: `${shipperList.length} Shippers`,
      },
    ];
  };

  onRowDataUpdated = () => {
    if (!this.state.shipperData) {
      this.gridApi?.hideOverlay();
      this.gridApi?.showLoadingOverlay();
    } else {
      this.gridApi?.hideOverlay();
    }
    const selectedCustomerCodes = this.selectedShippers.map(
      (shipper) => shipper?.pickupDetails?.rmaxShipperCode
    );
    this.gridApi.forEachNode?.((node) => {
      if (selectedCustomerCodes.includes(node.data.pickupDetails?.rmaxShipperCode)) {
        node.setSelected(true, false, true); // props are isSelected, clearOtherSelection, suppressFinishActions
      } else {
        // disabling supress finish actions calls onSelectionChanged event for each selection
        node.setSelected(false, false, true); // but we want single call of onSelectionChanged for multiselection , behaviour observed through user action
      } // so we suppress the call from setSelected and call it explicitly after loop completes
    });
    this.gridApi.dispatchEvent({
      type: "selectionChanged",
      api: this.gridApi,
    });
  };

  runCommand = (command, data) => {
    if (command === 'openGeoCodeModel') {
       /* once Latitude/Longitude get updated using geocoding popup in grid, we need to show updated marker in geocoding popup map */
      const selectedShipper = this.state.shipperData.find(
        (shipper) => data.code === shipper.code
      );
      this.setState({
        geoCodeCustomer: getCustomerManagementGeocodingData(selectedShipper, appConstants.CUSTOMER_TYPE.PICKUP)
      });
    }
  };

  closeGeocodeModal = (hasDataChanged = false) => {
    if (hasDataChanged) {
      this.getShipperData(false);
    }
    this.setState({
      geoCodeCustomer: null,
    });
  };


  render() {
    const shipperData = this.state.quickFilter
      ? getFilteredData(
          this.state.shipperData ?? [],
          this.state.quickFilter,
          new Date()
        )
      : this.state.shipperData?.slice();
    return (
      <Fragment>
        <DataGrid
          key={this.props.userData.userProfile.terminal.id}
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns}
          config={this.gridConfig.config}
          rowSelection={this.gridConfig.config.rowSelection}
          rowDeselection={this.gridConfig.config.rowDeselection}
          rowData={shipperData}
          onRowDataUpdated={this.onRowDataUpdated}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.onSelectionChanged}
          layoutContainer={this.props.glContainer}
          quickFilterText={this.state.quickFilterText}
          onCellClicked={this.onCellClicked}
          getRowId={(params) => params.data.code}
          panelLabel={(filteredData) => (
            <StatusBar
              data={this.constructStatusBar(filteredData)}
              filterGridData={this.updateGridData}
            />
          )}
          context={this.gridContext}
        />
          {this.state.geoCodeCustomer && (
          <GeoCodingModal
            customer={this.state.geoCodeCustomer}
            userData={this.props.userData.userProfile}
            close={this.closeGeocodeModal}
          />
        )}
      </Fragment>
    );
  }
}

export default compose(withUserProfile, withAppConfig, withSnackbar)(Shippers);
